import React, { useMemo } from 'react'
import type { DashboardCell, DashboardStatusCategory } from '@myapp/contract'
import { FormSection, SegmentedControl } from '../../../shared/ui'
import {
  filterCells,
  selectGewerkRows,
  selectKpis,
  selectMonthlySeries,
  selectStatusSlices,
  selectYearSummary,
  type DashboardCellFilter,
  type DashboardMetric,
} from '../../../entities/dashboard'
import { DashboardKpis } from './DashboardKpis'
import { DashboardSkeleton } from './DashboardSkeleton'
import { GewerkBars } from './GewerkBars'
import { SollIstChart } from './SollIstChart'
import { SollIstSummary } from './SollIstSummary'
import { StatusDonut } from './StatusDonut'

interface Props {
  cells: DashboardCell[]
  /** Every month of the selected period, "YYYY-MM", also those without data. */
  months: string[]
  filter: DashboardCellFilter
  metric: DashboardMetric
  isLoading: boolean
  isError: boolean
  /** New figures are loading while the previous ones are still shown. */
  isRefreshing: boolean
  pricingAvailable: boolean
  now: Date
  onToggleGewerk: (key: string) => void
  onToggleStatus: (category: DashboardStatusCategory) => void
  onMetricChange: (metric: DashboardMetric) => void
}

const METRIC_ITEMS: { label: string; value: DashboardMetric }[] = [
  { label: 'Aufträge', value: 'count' },
  { label: 'Kosten', value: 'cost' },
  { label: 'Stunden', value: 'hours' },
]

const METRIC_UNIT: Record<DashboardMetric, string> = {
  count: 'Aufträge (Anzahl)',
  cost: 'Euro (Wert €)',
  hours: 'Stunden',
}

const Empty: React.FC = () => (
  <div className="px-3 py-10 text-center font-['Roboto'] text-sm text-gray-400">Keine Ergebnisse</div>
)

export const DashboardOverview: React.FC<Props> = ({
  cells,
  months,
  filter,
  metric,
  isLoading,
  isError,
  isRefreshing,
  pricingAvailable,
  now,
  onToggleGewerk,
  onToggleStatus,
  onMetricChange,
}) => {
  // Each panel leaves out the filter it is itself the control for, so a selected bar or slice
  // dims its neighbours instead of removing them.
  const filtered = useMemo(() => filterCells(cells, filter), [cells, filter])
  const gewerkRows = useMemo(() => selectGewerkRows(filterCells(cells, filter, ['gewerk'])), [cells, filter])
  const statusCells = useMemo(() => filterCells(cells, filter, ['status']), [cells, filter])
  const statusSlices = useMemo(() => selectStatusSlices(statusCells), [statusCells])
  const kpis = useMemo(() => selectKpis(filtered), [filtered])
  const series = useMemo(() => selectMonthlySeries(filtered, months, metric), [filtered, months, metric])
  const years = useMemo(() => selectYearSummary(series), [series])

  if (isLoading) return <DashboardSkeleton />

  if (isError) {
    return (
      <div role="alert" className="rounded-[20px] bg-white px-4 py-10 text-center font-['Roboto'] text-sm text-red-500 md:rounded-[30px]">
        Fehler beim Laden der Daten
      </div>
    )
  }

  const statusTotal = statusSlices.reduce((s, x) => s + x.count, 0)

  return (
    <div className={`flex flex-col gap-8 transition-opacity ${isRefreshing ? 'opacity-60' : ''}`} aria-busy={isRefreshing}>
      {!pricingAvailable && (
        <div role="status" className="rounded-2xl border border-[#ED9E42] bg-white px-4 py-3 font-['Roboto'] text-sm text-gray-800">
          Gewerk und Kosten sind gerade nicht verfügbar, weil die Verbindung zu Immotion fehlt. Die Anzahl der Aufträge stimmt.
        </div>
      )}

      <DashboardKpis kpis={kpis} />

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-[1.1fr_1fr]">
        <FormSection title="Aufträge nach Gewerk">
          {gewerkRows.length ? (
            <GewerkBars rows={gewerkRows} selected={filter.gewerke as string[]} onToggle={onToggleGewerk} />
          ) : (
            <Empty />
          )}
        </FormSection>
        <FormSection title="Auswertung nach Status">
          {statusTotal ? (
            <StatusDonut slices={statusSlices} total={statusTotal} selected={filter.status} onToggle={onToggleStatus} />
          ) : (
            <Empty />
          )}
        </FormSection>
      </div>

      <FormSection title="Soll / Ist">
        <div className="flex flex-wrap items-center justify-between gap-x-6 gap-y-3">
          <SegmentedControl items={METRIC_ITEMS} value={metric} onChange={onMetricChange} ariaLabel="Kennzahl" />
          <ul className="flex items-center gap-4 font-['Roboto'] text-[13px] text-gray-700">
            <li className="inline-flex items-center gap-1.5">
              <i aria-hidden className="h-2.5 w-2.5 rounded-[3px] border border-[#124093] bg-[#E6F0FB]" />
              Soll
            </li>
            <li className="inline-flex items-center gap-1.5">
              <i aria-hidden className="h-2.5 w-2.5 rounded-[3px] bg-[#004494]" />
              Ist
            </li>
            <li className="text-gray-500">{METRIC_UNIT[metric]}</li>
          </ul>
        </div>
        {months.length ? (
          <div className="grid grid-cols-1 items-start gap-7 min-[1181px]:grid-cols-[minmax(0,1fr)_300px]">
            <SollIstChart series={series} metric={metric} now={now} />
            <div className="min-w-0 border-t border-gray-200 pt-5 min-[1181px]:border-l min-[1181px]:border-t-0 min-[1181px]:pl-7 min-[1181px]:pt-0">
              <SollIstSummary years={years} metric={metric} startsMidYear={months.some((m) => m.startsWith('2026'))} />
            </div>
          </div>
        ) : (
          <Empty />
        )}
      </FormSection>
    </div>
  )
}
